import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import impersonationService, {
  UserSearchResult,
  UserPermissionPreview,
} from '../../services/impersonation.service';
import { useImpersonation } from '../../contexts/ImpersonationContext';

interface UserImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserImpersonationModal: React.FC<UserImpersonationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [employeesOnly, setEmployeesOnly] = useState(true);
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [userPreview, setUserPreview] = useState<UserPermissionPreview | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const { startImpersonation, isLoading: isStarting } = useImpersonation();
  const [error, setError] = useState<string | null>(null);

  // empOnlyOverride lets the toggle re-search with its new value immediately
  // (setState is async, so we can't rely on `employeesOnly` in the same tick).
  const handleSearch = async (empOnlyOverride?: boolean) => {
    if (!searchQuery || searchQuery.length < 2) {
      setError('Please enter at least 2 characters to search');
      return;
    }

    const empOnly = empOnlyOverride ?? employeesOnly;
    try {
      setError(null);
      setIsSearching(true);
      setHasSearched(true);
      // Employee filtering happens server-side so the result cap applies after
      // the domain filter — see impersonation search endpoint.
      const results = await impersonationService.searchUsers(searchQuery, 20, empOnly);
      setSearchResults(results);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to search users');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectUser = async (user: UserSearchResult) => {
    setSelectedUser(user);
    setUserPreview(null);
    setError(null);

    try {
      setIsLoadingPreview(true);
      const preview = await impersonationService.previewUserPermissions(user.id);
      setUserPreview(preview);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load user permissions');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await startImpersonation(selectedUser.id);
      onClose();
      // Reload the page to refresh all permissions
      window.location.reload();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start impersonation');
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
    setSelectedUser(null);
    setUserPreview(null);
    setError(null);
    onClose();
  };

  // Alt+C closes via handleClose so the picker state resets, same as the X and
  // Cancel buttons. Starting an impersonation session stays click-only.
  useAltHotkeys({ C: handleClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <ModalHeader title="Impersonate User" onClose={handleClose} dragHandleProps={dragHandleProps} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Search Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search for a user
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter name or email..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <label className="flex items-center gap-2 whitespace-nowrap cursor-pointer select-none px-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={employeesOnly}
                  aria-label="Employees Only"
                  onClick={() => {
                    const next = !employeesOnly;
                    setEmployeesOnly(next);
                    // Re-run the search server-side with the new filter value.
                    if (searchQuery.trim().length >= 2) handleSearch(next);
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                    employeesOnly ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      employeesOnly ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-700">Employees Only</span>
              </label>
              <button
                onClick={() => handleSearch()}
                disabled={isSearching}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Search Results */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Search Results</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {searchResults.length === 0 && !isSearching && !hasSearched && (
                  <p className="text-gray-500 text-sm">No users to display. Start searching above.</p>
                )}
                {searchResults.length === 0 && !isSearching && hasSearched && (
                  <p className="text-gray-500 text-sm">
                    {employeesOnly
                      ? 'No employee (@gwii.com) users match your search.'
                      : 'No users match your search.'}
                  </p>
                )}
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedUser?.id === user.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{user.displayName}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                    {user.jobTitle && (
                      <div className="text-xs text-gray-500 mt-1">{user.jobTitle}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* User Preview */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">User Permissions Preview</h3>
              {!selectedUser && (
                <p className="text-gray-500 text-sm">Select a user to preview their permissions</p>
              )}

              {selectedUser && isLoadingPreview && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading permissions...</div>
                </div>
              )}

              {selectedUser && userPreview && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                  {/* User Info */}
                  <div>
                    <div className="font-semibold text-gray-900">{userPreview.displayName}</div>
                    <div className="text-sm text-gray-600">{userPreview.email}</div>
                    {userPreview.jobTitle && (
                      <div className="text-xs text-gray-500 mt-1">{userPreview.jobTitle}</div>
                    )}
                  </div>

                  {/* Global Admin Badge */}
                  {userPreview.is_global_admin && (
                    <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium inline-block">
                      Global Administrator
                    </div>
                  )}

                  {/* 360 Groups — only GWI360- prefixed groups */}
                  {(() => {
                    const groups360 = userPreview.groups.filter((g) =>
                      g.toUpperCase().startsWith('GWI360-')
                    );
                    return (
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          360 Groups ({groups360.length})
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {groups360.length === 0 ? (
                            <p className="text-xs text-gray-500">No 360 groups</p>
                          ) : (
                            groups360.map((group, idx) => (
                              <div key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                {group}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Accessible Routes */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Accessible Routes ({userPreview.accessible_routes.length})
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {userPreview.accessible_routes.length === 0 ? (
                        <p className="text-xs text-gray-500">No accessible routes</p>
                      ) : userPreview.accessible_routes.includes('*') ? (
                        <p className="text-xs text-green-600 font-medium">All routes (Admin)</p>
                      ) : (
                        userPreview.accessible_routes.map((route, idx) => (
                          <div key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                            {route}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleStartImpersonation}
            disabled={!selectedUser || !userPreview || isStarting}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isStarting ? 'Starting...' : 'Start Impersonation'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserImpersonationModal;
